import { useEffect, useState } from 'react';
import styled from 'styled-components';

const AppleLoginContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0;
  padding: 0;
`;

const AppleLoginButton = styled.button`
  /* 苹果官方设计规范 */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-width: 200px;
  height: 44px; /* 苹果推荐的最小触摸目标 */
  padding: 0 16px;
  
  /* 苹果官方颜色规范 */
  background: #000000;
  color: #ffffff;
  border: 1px solid #000000;
  border-radius: 6px; /* 苹果推荐圆角 */
  
  /* 苹果官方字体规范 */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 400; /* 苹果使用常规字重 */
  line-height: 1.2;
  letter-spacing: 0.01em;
  
  /* 交互状态 */
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  
  /* 悬停状态 */
  &:hover {
    background: #1a1a1a;
    border-color: #1a1a1a;
  }
  
  /* 激活状态 */
  &:active {
    background: #333333;
    border-color: #333333;
    transform: scale(0.98);
  }
  
  /* 禁用状态 */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #000000;
    border-color: #000000;
    
    &:hover {
      background: #000000;
      border-color: #000000;
      transform: none;
    }
  }
  
  /* 焦点状态（无障碍支持） */
  &:focus {
    outline: 2px solid #007AFF;
    outline-offset: 2px;
  }
  
  /* 苹果图标样式 */
  .apple-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 200px;
  padding: 12px 16px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  color: #333333;
`;

const LogoutButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d1d6;
  background: #ffffff;
  color: #333333;
  
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f7;
    border-color: #a1a1a6;
  }
  
  &:active {
    background: #e5e5ea;
    transform: scale(0.98);
  }
  
  &:focus {
    outline: 2px solid #007AFF;
    outline-offset: 2px;
  }
`;

const AppleLogin = ({ onLogin, onLogout, user, loading }) => {
  const [isAppleScriptLoaded, setIsAppleScriptLoaded] = useState(false);

  useEffect(() => {
    // 检查是否是Apple ID回调
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const idToken = urlParams.get('id_token');
    const state = urlParams.get('state');
    
    if (code && idToken && state === 'apple_login_state') {
      console.log('🍎 Apple ID redirect callback detected');
      console.log('🍎 Code:', code);
      console.log('🍎 ID Token:', idToken);
      console.log('🍎 State:', state);
      
      // 处理回调数据
      const authData = {
        authorization: {
          code: code,
          id_token: idToken,
          state: state
        },
        user: {
          // 从id_token中解析用户信息
          email: 'user@example.com' // 临时值，实际应该从token解析
        },
        provider: 'apple'
      };
      
      if (onLogin) {
        console.log('🍎 Calling onLogin with redirect data');
        onLogin(authData);
      }
      
      // 清理URL参数
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [onLogin]);

  useEffect(() => {
    // 检查Apple ID脚本是否已加载
    if (window.AppleID) {
      setIsAppleScriptLoaded(true);
      return;
    }

    // 动态加载Apple ID脚本
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.onload = () => {
      setIsAppleScriptLoaded(true);
      initializeAppleID();
    };
    script.onerror = () => {
      console.error('Failed to load Apple ID script');
    };
    document.head.appendChild(script);

    return () => {
      // 清理脚本
      const existingScript = document.querySelector('script[src*="appleid.auth.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // 单独注册事件监听器，确保每次组件挂载时都注册
  useEffect(() => {
    console.log('🍎 Registering Apple ID event listeners');
    
    // 注册标准事件监听器
    document.addEventListener('AppleIDSignInOnSuccess', handleAppleSignInSuccess);
    document.addEventListener('AppleIDSignInOnFailure', handleAppleSignInFailure);
    
    // 添加全局事件监听器来捕获所有可能的事件
    const globalEventHandler = (event) => {
      console.log('🍎 Global event captured:', event.type, event);
      if (event.type === 'AppleIDSignInOnSuccess') {
        handleAppleSignInSuccess(event);
      } else if (event.type === 'AppleIDSignInOnFailure') {
        handleAppleSignInFailure(event);
      }
    };
    
    document.addEventListener('AppleIDSignInOnSuccess', globalEventHandler, true);
    document.addEventListener('AppleIDSignInOnFailure', globalEventHandler, true);

    return () => {
      console.log('🍎 Removing Apple ID event listeners');
      document.removeEventListener('AppleIDSignInOnSuccess', handleAppleSignInSuccess);
      document.removeEventListener('AppleIDSignInOnFailure', handleAppleSignInFailure);
      document.removeEventListener('AppleIDSignInOnSuccess', globalEventHandler, true);
      document.removeEventListener('AppleIDSignInOnFailure', globalEventHandler, true);
    };
  }, []);

  const initializeAppleID = () => {
    if (window.AppleID && window.AppleID.auth) {
      try {
        // 使用环境变量配置回调URI（开发/生产）
        const redirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI || '';
        
        console.log('Current origin:', window.location.origin);
        console.log('Calculated redirectURI:', redirectURI);
        
        const clientId = process.env.REACT_APP_APPLE_CLIENT_ID || 'ai.enjoy.service.dev';
        
        console.log('Apple ID Config:', {
          clientId: clientId,
          redirectURI: redirectURI,
          origin: window.location.origin
        });
        
        // 延迟初始化，确保DOM完全加载
        setTimeout(() => {
          window.AppleID.auth.init({
            clientId: clientId,
            scope: 'name email',
            redirectURI: redirectURI,
            state: 'apple_login_state',
            nonce: 'apple_login_nonce',
            usePopup: false  // 使用重定向模式而不是弹窗
          });
        }, 100);
      } catch (error) {
        console.error('Failed to initialize Apple ID:', error);
      }
    }
  };

  const handleAppleSignInSuccess = (event) => {
    console.log('🍎 Apple Sign In Success Event:', event);
    console.log('🍎 Event Detail Data:', event.detail);
    console.log('🍎 Event Detail Data Type:', typeof event.detail);
    
    // 检查数据结构
    if (event.detail && event.detail.data) {
      const { authorization, user: userData } = event.detail.data;
      console.log('🍎 Authorization:', authorization);
      console.log('🍎 User Data:', userData);
      
      if (onLogin) {
        console.log('🍎 Calling onLogin callback...');
        onLogin({
          authorization,
          user: userData,
          provider: 'apple'
        });
      } else {
        console.warn('🍎 No onLogin callback provided');
      }
    } else {
      console.error('🍎 Invalid event data structure:', event.detail);
    }
  };

  const handleAppleSignInFailure = (event) => {
    console.error('Apple Sign In Failure:', event.detail.error);
  };

  const handleAppleLogin = async () => {
    if (!isAppleScriptLoaded || !window.AppleID) {
      console.error('Apple ID script not loaded');
      return;
    }

    try {
      // 检查Apple ID是否已初始化
      if (!window.AppleID.auth) {
        console.error('Apple ID auth not initialized');
        return;
      }
      
      await window.AppleID.auth.signIn();
    } catch (error) {
      console.error('Apple Sign In Error:', error);
      // 如果是403错误，可能是配置问题
      if (error.message && error.message.includes('403')) {
        console.error('403 Forbidden - Check Apple Developer Console configuration');
      }
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  if (user) {
    return (
      <AppleLoginContainer>
        <UserInfo>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* 用户头像占位符 */}
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#007AFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {user.name?.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
            </div>
            <span>
              {user.name?.firstName && user.name?.lastName 
                ? `${user.name.firstName} ${user.name.lastName}`
                : user.email || 'Apple User'
              }
            </span>
          </div>
          <LogoutButton onClick={handleLogout} type="button" aria-label="Sign out">
            Sign Out
          </LogoutButton>
        </UserInfo>
      </AppleLoginContainer>
    );
  }

  return (
    <AppleLoginContainer>
      <AppleLoginButton 
        onClick={handleAppleLogin}
        disabled={loading || !isAppleScriptLoaded}
        type="button"
        aria-label="Sign in with Apple"
      >
        {/* 苹果官方SVG图标 */}
        <svg className="apple-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
        {loading ? 'Signing in...' : 'Sign in with Apple'}
      </AppleLoginButton>
    </AppleLoginContainer>
  );
};

export default AppleLogin;
